import { growthFactor } from "@/lib/tool-kit/finance/tvm";

/**
 * Simple interest: I = P · r · t, with the rate nominal per year and t in years.
 *
 * Time can be entered in years, months (÷ 12) or days (÷ the day-count basis, 365 or
 * 360). Which basis applies depends on the contract; the calculator makes the reader
 * choose rather than assume.
 */

export type SimpleSolve = "interest" | "principal" | "rate" | "time";
export type TimeUnit = "years" | "months" | "days";

export interface SimpleKnowns {
  principal: number | null;
  /** Annual rate as a decimal. */
  rate: number | null;
  /** Time in years. */
  years: number | null;
  interest: number | null;
}

export interface SimpleResult {
  principal: number;
  rate: number;
  years: number;
  interest: number;
  amount: number;
}

export function toYears(
  value: number,
  unit: TimeUnit,
  dayBasis: number
): number {
  if (unit === "months") {
    return value / 12;
  }
  if (unit === "days") {
    return value / dayBasis;
  }
  return value;
}

export function fromYears(
  years: number,
  unit: TimeUnit,
  dayBasis: number
): number {
  if (unit === "months") {
    return years * 12;
  }
  if (unit === "days") {
    return years * dayBasis;
  }
  return years;
}

function positive(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}

/** Solves for the chosen term; null when the other three do not determine it. */
export function solveSimple(
  mode: SimpleSolve,
  known: SimpleKnowns
): SimpleResult | null {
  const { principal, rate, years, interest } = known;
  let p = principal;
  let r = rate;
  let t = years;
  let i = interest;
  if (mode === "interest" && positive(p) && positive(r) && positive(t)) {
    i = p * r * t;
  } else if (
    mode === "principal" &&
    positive(i) &&
    positive(r) &&
    positive(t)
  ) {
    p = i / (r * t);
  } else if (mode === "rate" && positive(p) && positive(i) && positive(t)) {
    r = i / (p * t);
  } else if (mode === "time" && positive(p) && positive(r) && positive(i)) {
    t = i / (p * r);
  } else {
    return null;
  }
  if (p === null || r === null || t === null || i === null) {
    return null;
  }
  return { principal: p, rate: r, years: t, interest: i, amount: p + i };
}

export interface ComparisonRow {
  years: number;
  simple: number;
  compound: number;
}

const MAX_WHOLE_YEARS = 30;

/** Simple vs compound balance at each whole year (to 30) and at the end of the term. */
export function compareSimpleCompound(
  principal: number,
  rate: number,
  years: number,
  perYear: number
): ComparisonRow[] {
  const at = (t: number): ComparisonRow => ({
    years: t,
    simple: principal * (1 + rate * t),
    compound: principal * growthFactor(rate, perYear, t),
  });
  const rows: ComparisonRow[] = [];
  const whole = Math.min(Math.floor(years), MAX_WHOLE_YEARS);
  for (let y = 1; y <= whole; y += 1) {
    rows.push(at(y));
  }
  if (years - whole > 1e-9) {
    rows.push(at(years));
  }
  return rows;
}
